import { defineComponent as _defineComponent } from 'vue'
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, resolveComponent as _resolveComponent, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "module-switch-container" }
const _hoisted_2 = { class: "module-switch-button-wrapper" }
const _hoisted_3 = {
  key: 1,
  src: "/golf_logo.svg",
  alt: "Golf",
  class: "module-icon module-logo"
}
const _hoisted_4 = { class: "module-label" }
const _hoisted_5 = { class: "menu-item-align" }

import { computed, markRaw, onMounted } from 'vue'
import { LocationQueryRaw, useRouter } from 'vue-router'
import { User, Bicycle, ArrowDown } from '@element-plus/icons-vue'

import { UserStatus, UserType } from '@/models/user'
import { CarProfileStatus } from '@/models/carProfile'

import { getDefaultPaginationQuery } from '@/constants/pagination'
import { routeNames } from '@/router/route-names'
import {
  getScope,
  RbacAction,
  RbacEntity,
  hasEvAccess as checkEvAccess,
  hasGolfAccess as checkGolfAccess,
  hasP2pAccess as checkP2pAccess,
} from '@/helpers/rbac'
import { commonCase } from '@/helpers/strings'

import { useAuthStore } from '@/store/auth'
import { LsGlobalKeys, lsGet } from '@/helpers/local-storage'

import useCurrentRouteName from '@/composables/useCurrentRouteName'
import useCurrentQuery from '@/composables/useCurrentQuery'
import { TripStatus } from '@/models/trip'


interface MenuItem {
  label: string
  routeName?: string
  filter?: boolean
  icon?: string
  query?: LocationQueryRaw
  scope?: string
  nested: MenuItem[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MainMenu',
  props: {
    collapse: { type: Boolean },
    showGolfModule: { type: Boolean }
  },
  emits: ["switchModule"],
  setup(__props: any) {





const authStore = useAuthStore()

const authScopes = authStore.scopes




const currentModule = computed(() => {
  const raw = lsGet(LsGlobalKeys.CURRENT_MODULE)
  let saved: string | null = null
  if (raw) {
    try {
      saved = JSON.parse(raw)
    } catch {
      saved = raw
    }
  }
  return saved === 'ev' || saved === 'golf' ? saved : 'p2p'
})

// Auto-refresh session if ev_status is undefined (old session)
onMounted(() => {
  const currentUser = authStore.session?.user
  // Removed auto-refresh logic that was causing logout on production
  // Users without ev_status should still be able to use the P2P module
})

const router = useRouter()
const activeRoute = useCurrentRouteName()
const activeQuery = useCurrentQuery()

const menuOptions: MenuItem[] = [
  {
    label: 'Registry',
    icon: 'el-icon-document',
    nested: [
      {
        label: 'Users',
        routeName: routeNames.users,
        scope: getScope(RbacAction.READ, RbacEntity.USERS),
        query: getDefaultPaginationQuery(),
        nested: [],
      },
      {
        label: 'Vehicles',
        routeName: routeNames.vehicles,
        scope: getScope(RbacAction.READ, RbacEntity.CARS),
        query: getDefaultPaginationQuery(),
        nested: [],
      },
    ],
  },
  {
    label: 'Journey',
    icon: 'el-icon-guide',
    nested: [
      {
        label: 'Trips',
        routeName: routeNames.trips,
        scope: getScope(RbacAction.READ, RbacEntity.TRIPS),
        query: getDefaultPaginationQuery(),
        nested: [],
      },
    ],
  },
  {
    label: 'Reviews',
    icon: 'el-icon-star-off',
    nested: [
      {
        label: 'Host reviews',
        routeName: routeNames.reviews,
        scope: getScope(RbacAction.READ, RbacEntity.USERS),
        query: { ...getDefaultPaginationQuery(), userType: UserType.HOST },
        nested: [],
      },
      {
        label: 'Renter reviews',
        routeName: routeNames.reviews,
        scope: getScope(RbacAction.READ, RbacEntity.USERS),
        query: {
          ...getDefaultPaginationQuery(),
          userType: UserType.RENTER,
        },
        nested: [],
      },
    ],
  },
  {
    label: 'Pre-set filters',
    icon: 'el-icon-set-up',
    routeName: routeNames.settings,
    nested: [
      {
        label: `Today's check-in trips`,
        routeName: routeNames.trips,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.TRIPS),
        query: {
          ...getDefaultPaginationQuery(),
          checkIn: 'true',
          statuses: [TripStatus.ACCEPTED],
        },
        nested: [],
      },
      {
        label: `Today's check-out trips`,
        routeName: routeNames.trips,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.TRIPS),
        query: { ...getDefaultPaginationQuery(), checkOut: 'true' },
        nested: [],
      },
      {
        label: 'Cancelled trip list',
        routeName: routeNames.trips,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.TRIPS),
        query: { ...getDefaultPaginationQuery(), cancelled: 'true' },
        nested: [],
      },
      {
        label: 'On hold trip list',
        routeName: routeNames.trips,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.TRIPS),
        query: {
          ...getDefaultPaginationQuery(),
          statuses: [TripStatus.ON_HOLD],
        },
        nested: [],
      },
      {
        label: 'Damage report list',
        routeName: routeNames.trips,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.TRIPS),
        query: { ...getDefaultPaginationQuery(), damaged: 'true' },
        nested: [],
      },
      {
        label: 'Trip Finalization Tasks List',
        routeName: routeNames.trips,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.TRIPS),
        query: { ...getDefaultPaginationQuery(), isFinalize: 'true' },
        nested: [],
      },
      {
        label: 'User verification list',
        routeName: routeNames.users,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.USERS),
        query: {
          ...getDefaultPaginationQuery(),
          status: UserStatus.verifying,
        },
        nested: [],
      },
      {
        label: 'Misconducted users list',
        routeName: routeNames.users,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.USERS),
        query: {
          ...getDefaultPaginationQuery(),
          status: UserStatus.active,
          misconduct: 'true',
        },
        nested: [],
      },
      {
        label: 'Vehicle verification list',
        routeName: routeNames.vehicles,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.CARS),
        query: {
          ...getDefaultPaginationQuery(),
          status: CarProfileStatus.verifying,
        },
        nested: [],
      },
      {
        label: 'Accounting Month-End Task List',
        routeName: routeNames.users,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.USERS),
        query: {
          ...getDefaultPaginationQuery(),
          isBalance: 'true',
        },
        nested: [],
      },
      {
        label: 'Failed credit card operations',
        routeName: routeNames.trips,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.TRIPS),
        query: {
          ...getDefaultPaginationQuery(),
          failedPayments: 'true',
        },
        nested: [],
      },
      {
        label: 'Failed Insurance API Script Runs',
        routeName: routeNames.trips,
        filter: true,
        scope: getScope(RbacAction.READ, RbacEntity.TRIPS),
        query: {
          ...getDefaultPaginationQuery(),
          insurance_scripts: 'failed',
        },
        nested: [],
      },
    ],
  },
  {
    label: 'Settings',
    icon: 'el-icon-setting',
    routeName: routeNames.settings,
    scope: getScope(RbacAction.READ, RbacEntity.APP_SETTINGS),
    nested: [],
  },
  {
    label: 'Security Groups',
    icon: 'el-icon-lock',
    routeName: routeNames.securityGroups,
    scope: getScope(RbacAction.READ, RbacEntity.APP_SETTINGS),
    nested: [],
  },
  {
    label: 'Notifications',
    icon: 'el-icon-bell',
    routeName: routeNames.notifications,
    scope: getScope(RbacAction.READ, RbacEntity.APP_SETTINGS),
    nested: [],
  },
  {
    label: 'Car Data',
    icon: 'el-icon-folder',
    routeName: routeNames.carData,
    scope: getScope(RbacAction.READ, RbacEntity.APP_SETTINGS),
    nested: [],
  },
  {
    label: 'Localization',
    icon: 'el-icon-document',
    routeName: routeNames.localization,
    scope: getScope(RbacAction.READ, RbacEntity.APP_SETTINGS),
    nested: [],
  },
  {
    label: 'Localization Golf module',
    icon: 'el-icon-document',
    routeName: routeNames.golfLocalization,
    scope: getScope(RbacAction.READ, RbacEntity.APP_SETTINGS),
    nested: [],
  },
]

const navigate = (
  routeName: string | undefined,
  query: LocationQueryRaw | undefined
) => {
  if (routeName) router.push({ name: routeName, query })
}

const signOut = async () => {
  await authStore.signOut()
  router.push({ name: routeNames.login })
}

const hasP2pAccess = computed(() => checkP2pAccess(authStore.session?.user))
const hasEvAccess = computed(() => checkEvAccess(authStore.session?.user))
const hasGolfAccess = computed(() => checkGolfAccess(authStore.session?.user))

const checkIfActive = (
  routeName: string | undefined,
  query?: LocationQueryRaw
): boolean => {
  if (!routeName) return false

  const namesToCheck = [routeName]
  const additionalChecks: boolean[] = []

  // Push additional conditions if needed
  if (routeName === routeNames.trips) {
    namesToCheck.push(routeNames.tripDetails)
  }
  if (routeName === routeNames.users) {
    namesToCheck.push(routeNames.userDetails)
  }
  if (routeName === routeNames.vehicles) {
    namesToCheck.push(routeNames.vehicleDetails)
  }
  if (routeName === routeNames.reviews) {
    additionalChecks.push(activeQuery.value.userType === query?.userType)
  }

  if (!activeRoute.value) return false

  const isActiveRoute = namesToCheck.includes(activeRoute.value as string)

  return isActiveRoute && additionalChecks.every(check => check)
}

const currentModuleIcon = computed(() => {
  const icons: Record<string, any> = {
    p2p: markRaw(User),
    ev: markRaw(Bicycle),
  }
  return icons[currentModule.value] || markRaw(User)
})

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_dropdown_item = _resolveComponent("el-dropdown-item")!
  const _component_el_dropdown_menu = _resolveComponent("el-dropdown-menu")!
  const _component_el_dropdown = _resolveComponent("el-dropdown")!
  const _component_el_menu_item = _resolveComponent("el-menu-item")!
  const _component_el_tooltip = _resolveComponent("el-tooltip")!
  const _component_el_sub_menu = _resolveComponent("el-sub-menu")!
  const _component_el_menu = _resolveComponent("el-menu")!

  return (_openBlock(), _createBlock(_component_el_menu, {
    "unique-opened": false,
    collapse: _ctx.collapse,
    "collapse-transition": false,
    mode: "vertical",
    class: "el-menu-vertical p2p-menu"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_el_dropdown, {
          onCommand: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('switchModule', $event)))
        }, {
          dropdown: _withCtx(() => [
            _createVNode(_component_el_dropdown_menu, null, {
              default: _withCtx(() => [
                (hasP2pAccess.value)
                  ? (_openBlock(), _createBlock(_component_el_dropdown_item, {
                      key: 0,
                      command: "p2p"
                    }, {
                      default: _withCtx(() => _cache[1] || (_cache[1] = [
                        _createTextVNode("P2P MODULE")
                      ])),
                      _: 1,
                      __: [1]
                    }))
                  : _createCommentVNode("", true),
                (hasEvAccess.value)
                  ? (_openBlock(), _createBlock(_component_el_dropdown_item, {
                      key: 1,
                      command: "ev"
                    }, {
                      default: _withCtx(() => _cache[2] || (_cache[2] = [
                        _createTextVNode("EV MODULE")
                      ])),
                      _: 1,
                      __: [2]
                    }))
                  : _createCommentVNode("", true),
                (hasGolfAccess.value && _ctx.showGolfModule)
                  ? (_openBlock(), _createBlock(_component_el_dropdown_item, {
                      key: 2,
                      command: "golf"
                    }, {
                      default: _withCtx(() => _cache[3] || (_cache[3] = [
                        _createTextVNode(" GOLF MODULE ")
                      ])),
                      _: 1,
                      __: [3]
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_el_button, {
                class: _normalizeClass(["module-switch-button", [`is-${currentModule.value}`]]),
                type: "primary"
              }, {
                default: _withCtx(() => [
                  (currentModule.value !== 'golf')
                    ? (_openBlock(), _createBlock(_component_el_icon, {
                        key: 0,
                        class: "module-icon"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(), _createBlock(_resolveDynamicComponent(currentModuleIcon.value)))
                        ]),
                        _: 1
                      }))
                    : (_openBlock(), _createElementBlock("img", _hoisted_3)),
                  _createElementVNode("span", _hoisted_4, _toDisplayString(currentModule.value.toUpperCase()) + " MODULE", 1),
                  _createVNode(_component_el_icon, { class: "dropdown-arrow" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(ArrowDown))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["class"])
            ])
          ]),
          _: 1
        })
      ]),
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(menuOptions, (option, parentIndex) => {
        return (_openBlock(), _createElementBlock(_Fragment, {
          key: option.label
        }, [
          (!!option.nested.length)
            ? (_openBlock(), _createBlock(_component_el_sub_menu, {
                key: 0,
                index: `${parentIndex}`
              }, {
                title: _withCtx(() => [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("i", {
                      class: _normalizeClass(option.icon)
                    }, null, 2),
                    _createElementVNode("span", null, _toDisplayString(option.label), 1)
                  ])
                ]),
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(option.nested, ({ routeName, scope, query, filter, label }, index) => {
                    return (_openBlock(), _createBlock(_component_el_tooltip, {
                      key: routeName,
                      placement: "right",
                      disabled: !scope || _unref(authScopes).includes(scope),
                      content: `Not enough rights (${_unref(commonCase)(scope || '')})`
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_menu_item, {
                          disabled: !!scope && !_unref(authScopes).includes(scope),
                          index: `${parentIndex}-${index}`,
                          onClick: ($event: any) => (navigate(routeName, query)),
                          class: _normalizeClass({
              'active-route': filter ? false : checkIfActive(routeName, query),
            })
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(label), 1)
                          ]),
                          _: 2
                        }, 1032, ["disabled", "index", "onClick", "class"])
                      ]),
                      _: 2
                    }, 1032, ["disabled", "content"]))
                  }), 128))
                ]),
                _: 2
              }, 1032, ["index"]))
            : (_openBlock(), _createBlock(_component_el_tooltip, {
                key: 1,
                placement: "right",
                disabled: !option.scope || _unref(authScopes).includes(option.scope),
                content: `Not enough rights (${_unref(commonCase)(option.scope || '')})`
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_menu_item, {
                    disabled: !!option.scope && !_unref(authScopes).includes(option.scope),
                    index: `${parentIndex}`,
                    onClick: ($event: any) => (navigate(option.routeName, option.query)),
                    class: _normalizeClass({
            'active-route': option.filter
              ? false
              : checkIfActive(option.routeName, option.query),
          })
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("i", {
                        class: _normalizeClass(`${option.icon} menu-icon`)
                      }, null, 2),
                      _createElementVNode("span", null, _toDisplayString(option.label), 1)
                    ]),
                    _: 2
                  }, 1032, ["disabled", "index", "onClick", "class"])
                ]),
                _: 2
              }, 1032, ["disabled", "content"]))
        ], 64))
      }), 64))
    ]),
    _: 1
  }, 8, ["collapse"]))
}
}

})